import { BreakpointObserver } from '@angular/cdk/layout';
import { trackEvent } from 'src/app/core/analytics';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  of,
  startWith,
} from 'rxjs';
import { MaterialModule } from 'src/app/core/material.module';
import { NewPatientDialogComponent } from 'src/app/features/patients/components/new-patient-dialog/new-patient-dialog.component';
import { NewTestDialogComponent } from 'src/app/features/patients/components/new-test-dialog/new-test-dialog.component';
import { PatientsService } from 'src/app/features/patients/services/patients.service';
import { BIG_SCREEN, RolesEnum } from 'src/app/features/tests/features/constants';
import { ROLE_LABEL_MAP } from 'src/app/features/user-management/services/constants';
import { AbilityService } from '../../services/ability/ability.service';
import {
  AppNotificationsStore,
  AppNotificationsStoreStoreService,
} from '../../services/app-notifications-store.service';
import { AppStatsStoreStoreService } from '../../services/app-stats-store.service';
import { AppService } from '../../services/app.service';
import { LogoutService } from '../../services/logout.service';
import { NotificationService } from '../../services/notification.service';
import { ServerNotificationService } from '../../services/server-notification.service';
import { SessionService } from '../../services/session/session.service';
import { SwUpdateService } from '../../services/sw-update.service';
import { UserService } from '../../services/user.service';
import { NotificationDialogComponent } from './components/notification/notification-dialog.component';
import { ProfileDialogComponent } from './components/profile/profile-dialog.component';
import { TestInfoComponent } from './components/test-info/test-info.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    MaterialModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TestInfoComponent,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  currUrl: string;
  currUser: any;
  currTest: any;
  patientDetails: any;
  packetLockEvent: any;
  testId: string;
  packetLogs: boolean = false;
  networkStatus: boolean = true;
  networkStatus$: Subscription = Subscription.EMPTY;
  public origin: boolean;
  public isCurrentCompActive = 0;
  public liveTestsApiOptions = {
    q: '',
    location: '',
  };
  public liveTestsNum = 0;
  public notificationStore: AppNotificationsStore;
  public isSharedPhysician = false;
  // The new-version bar is fixed to the top; push the header below it.
  get updateBarVisible(): boolean {
    return this.swUpdateService.prompt() === 'bar';
  }

  // isSSO and mainLabUrl are live getters off AbilityService (sticky from
  // sessionStorage). Don't cache them — refresh-token cycles would otherwise
  // leave a stale snapshot here.
  get isSSO(): boolean {
    return this.ability.isSSO;
  }
  get mainLabUrl(): string | null {
    return this.ability.mainLabUrl;
  }
  searchControl = new FormControl('');
  filteredPatients$: Observable<any[]>;
  private patientsSubject = new BehaviorSubject<any[]>([]);
  private subscriptions: Subscription[] = [];
  isBigScreen$: Observable<boolean>;
  constructor(
    public notificationService: NotificationService,
    public ability: AbilityService,
    private breakpointObserver: BreakpointObserver,
    public userService: UserService,
    private appStatsStoreStoreService: AppStatsStoreStoreService,
    private appNotificationsStoreStoreService: AppNotificationsStoreStoreService,
    public router: Router,
    private appService: AppService,
    private route: ActivatedRoute,
    private patientsService: PatientsService,
    private dialog: MatDialog,
    public serverNotificationService: ServerNotificationService,
    public sessionService: SessionService,
    private logoutService: LogoutService,
    private swUpdateService: SwUpdateService,
  ) {
    // Track current component
    this.router.events.subscribe((res: NavigationEnd) => {
      if (res instanceof NavigationEnd) {
        const componentArray = res.url.toString().split('/');
        this.isCurrentCompActive = componentArray.length;
      }
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((res: NavigationEnd) => {
        const url = res.url.toString();
        const componentArray = url.split('/');
        this.isCurrentCompActive = componentArray.length;

        if (url.includes('trend-analysis/') && componentArray.length > 2) {
          const id = componentArray[2];
          this.loadPatientDetailsForHeader(id);
        } else {
          if (!this.currTest?.patient) {
            this.patientDetails = null;
          }
        }
      });
  }

  getSalutation() {
    if (!this.currUser) return '';
    return (
      [this.currUser?.firstName, this.currUser?.lastName]
        .filter((name) => !!name)
        .join(' ') +
      (this.currUser?.role ? ' as ' + ROLE_LABEL_MAP[this.currUser.role] : '')
    );
  }

  openProfileDialog() {
    trackEvent('modal_opened', { modal: 'profile', feature: 'profile' });
    this.dialog.open(ProfileDialogComponent, {
      width: '400px',
    });
  }

  ngOnInit() {
    this.currUser = this.userService.currentUser.getValue();
    this.currUrl = this.router.url;
    this.isSharedPhysician = this.ability.role === RolesEnum.SharedPhysician;
    this.isBigScreen$ = this.breakpointObserver
      .observe(`(min-width: ${BIG_SCREEN}px)`)
      .pipe(map((result) => result.matches));

    if (this.ability.canView(this.ability.subjects.LiveTestsPage)) {
      this.appStatsStoreStoreService.stats$.subscribe((res) => {
        this.liveTestsNum = res.stats.liveTests;
      });
    }

    if (!this.isSharedPhysician) {
      this.appNotificationsStoreStoreService.notifications$.subscribe((res) => {
        this.notificationStore = res;
      });
    }

    this.appService.currTest.subscribe((test) => {
      this.currTest = test;
      if (test?.patient) {
        this.patientDetails = test.patient;
      } else {
        if (!this.router.url.includes('trend-analysis/')) {
          this.patientDetails = null;
        }
      }
    });

    this.route.queryParams.subscribe((params) => {
      this.origin = params['origin'];
    });

    if (
      this.ability.isLoggedIn &&
      this.ability.canView(this.ability.subjects.PatientsPage)
    ) {
      this.patientsService.getPatientDropdown().subscribe((patients) => {
        this.patientsSubject.next(patients.items);
      });
    }

    // Set up search filtering on the cached patients
    this.filteredPatients$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      filter((value) => typeof value === 'string'),
      filter((value) => value.length >= 1 || value.length === 0),
      map((searchText) => this.filterPatients(searchText)),
    );

    this.checkNetworkStatus();
    if (this.ability.isLoggedIn) {
      if (this.ability.canView(this.ability.subjects.DashboardPage)) {
        this.appStatsStoreStoreService.refreshStats(false);
      }
      if (!this.isSharedPhysician) {
        this.appNotificationsStoreStoreService.refreshNotifications(true);
      }
    }

    this.subscribeToEvents();
  }

  subscribeToEvents() {
    if (!this.isSharedPhysician) {
      this.appService.eventEmitter.subscribe((d) => {
        if (d == 'TESTS:NEW_TEST_CREATED') {
          this.appStatsStoreStoreService.refreshStats(true);
        }
        if (d == 'TESTS:TEST_DELETED') {
          this.appStatsStoreStoreService.refreshStats(true);
        }
        if (d == 'DEVICE:UPDATED_STATUS') {
          this.appStatsStoreStoreService.refreshStats(true);
        }
      });

      this.serverNotificationService.getTestNotifications().subscribe((data) => {
        if (data.type == 'TEST' && data.action == 'STARTED') {
          this.appStatsStoreStoreService.refreshStats(true);
        }
        if (data.type == 'TEST' && data.action == 'ENDED') {
          this.appStatsStoreStoreService.refreshStats(true);
        }
        if (data.type == 'ALGO') {
          this.appNotificationsStoreStoreService.refreshNotifications(true);
          if(this.currTest?.id == data.testId) {
            const getStatus = (arr: any[], key: string) =>
              arr?.find(obj => key in obj)?.[key];

            const currentMultiNightStatus = getStatus(this.currTest?.algoStatus || [], "multi_night");
            const newMultiNightStatus = getStatus(data.algoStatus || [], "multi_night");

            if (currentMultiNightStatus !== "Success" && newMultiNightStatus === "Success") {
              this.notificationService.success(
                'Multi night report finished successfully',
              );
            }

            this.currTest.algoStatus =  data.algoStatus
            this.appService.currTest.next({ ...this.currTest })

            this.appService.eventEmitter.next({
              type: 'MULTI_NIGHT_SUCCESS',
              testId: data.testId,
            });

          }
        }
      });
    }
    this.serverNotificationService.getUserNotifications().subscribe((data) => {
      console.log('Received user notification in HeaderComponent', data);
      if(data.type === 'USER_UPDATED') {
        this.sessionService.logout('You have been logged out due to changes in your user profile. Please log in again.');
      }

    });
    if (this.ability.isLoggedIn && !this.isSharedPhysician) {
      this.appStatsStoreStoreService.departmentId$.subscribe((deptId) => {
        this.appStatsStoreStoreService.refreshStats(false);
      });
    }
  }

  checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline'),
    )
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.networkStatus = status;
      });
  }

  logout() {
    this.sessionService.logout();
  }

  goBackToMainLab() {
    if (!this.mainLabUrl) return;
    window.location.href = this.mainLabUrl;
  }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  openNewPatientDialog() {
    if (this.sessionService.ensureMonitoringMode()) {
      return;
    }
    trackEvent('modal_opened', { modal: 'new_patient', feature: 'patients' });
    const dialogRef = this.dialog.open(NewPatientDialogComponent, {
      width: '640px',
      height: '100vh',
      maxWidth: '100vw',
      position: { right: '0' },
      panelClass: 'right-side-dialog',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Handle the form submission
      }
    });
  }

  openNewTestDialog(listItem: any): void {
    const patient = {
      name: listItem.user.firstName + ' ' + listItem.user.lastName,
      id: listItem.patientId,
      age: listItem.age,
      testsCount: listItem.testsCount,
      consumedTestsCount: listItem.consumedTestsCount,
      birthdayDate: listItem.birthdayDate,
    };
    trackEvent('modal_opened', { modal: 'new_test', feature: 'tests' });
    this.dialog
      .open(NewTestDialogComponent, {
        data: { patient },
        width: '640px',
        height: '100vh',
        maxWidth: '100vw',
        position: { right: '0' },
        panelClass: 'right-side-dialog',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          // create new Test result
          console.log(res);
        }
      });
  }

  private filterPatients(searchText: string): any[] {
    const filterValue = searchText.toLowerCase().trim();
    const currentPatients = this.patientsSubject.getValue();

    return currentPatients.filter((patient) => {
      const firstName = patient.user.firstName
        ? patient.user.firstName.toLowerCase()
        : '';
      const lastName = patient.user.lastName
        ? patient.user.lastName.toLowerCase()
        : '';

      // Create different name combinations
      const fullNameForward = `${firstName} ${lastName}`;
      const fullNameReverse = `${lastName} ${firstName}`;

      // Split search text into parts
      const searchParts = filterValue
        .split(' ')
        .filter((part) => part.length > 0);

      if (searchParts.length === 0) return true;

      if (searchParts.length === 1) {
        // Single word search - match against first or last name
        const searchTerm = searchParts[0];
        return firstName.includes(searchTerm) || lastName.includes(searchTerm);
      } else {
        // Multi-word search - try different combinations
        const [firstPart, secondPart, ...rest] = searchParts;

        // Check forward and reverse full name matches
        const forwardMatch = fullNameForward.includes(filterValue);
        const reverseMatch = fullNameReverse.includes(filterValue);

        // Check partial matches in both directions
        const partialForwardMatch =
          firstName.includes(firstPart) && lastName.includes(secondPart);
        const partialReverseMatch =
          lastName.includes(firstPart) && firstName.includes(secondPart);

        return (
          forwardMatch ||
          reverseMatch ||
          partialForwardMatch ||
          partialReverseMatch
        );
      }
    });
  }

  notifications() {
    trackEvent('modal_opened', { modal: 'notifications' });
    this.dialog.open(NotificationDialogComponent, {
      width: '600px',
    });
  }

  onPatientSelected(patient: any): void {
    if (this.sessionService.ensureMonitoringMode()) {
      return;
    }
    if (!patient) return;
    this.openNewTestDialog(patient);
    this.searchControl.setValue(''); // Clear input
  }

  loadPatientDetailsForHeader(id: string): void {
    if (!id) {
      this.patientDetails = null;
      return;
    }

    this.patientsService.getPatientById(id).subscribe({
      next: (patient) => {
        if (patient) {
          this.patientDetails = patient;
        } else {
          this.patientDetails = null;
        }
      },
      error: (error) => {
        console.error('Error loading patient details for header:', error);
        this.patientDetails = null;
      },
    });
  }

  ngOnDestroy() {
    if (this.networkStatus$) {
      this.networkStatus$.unsubscribe();
    }
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.packetLogs = null;
    this.testId = null;
  }

  protected readonly open = open;
}
